import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import api from "../../Utils/Axios";
import ExpenseLedger from "./ExpenseLedger";
import BankKhata from "./BankKhata";
import ReceivableKhata from "./ReceivableKhata";
import PayableKhata from "./PayableKhata";
import ProfitLossAccount from "./ProfitLossAccount";
import ReceiptManagement from "./ReceiptManagement";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DashboardHeader from "../CompanyComponents/DashboardHeaderRouter";
import OverviewTab from "./OverviewTab";
import BillsTab from "./BillsTab";
import ProfileTab from "./ProfileTab";
import PersonsTab from "./PersonsTab";
import SalesTab from "./SalesTab";
import RecoveriesTab from "./RecoveriesTab";
import ReceiptsTab from "./ReceiptsTab";
import CreateBillModal from "./CreateBillModal";
import { useBillHandlers } from "./useBillHandlers";
import { useCompanyProfile } from "./useCompanyProfile";
import CustomerKhata from "./CustomerKhata";
import SupplierKhata from "./SupplierKhata";
import LoadingScreen from "../../Components/LoadingScreen";

function CompanyDashboardPart2() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState(() => {
    const cached = localStorage.getItem("companyData");
    return cached ? JSON.parse(cached) : null;
  });
  const [isLoading, setIsLoading] = useState(!localStorage.getItem("companyData")); // Only load if no cache
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "overview");
  const [activeLedgerTab, setActiveLedgerTab] = useState("customerKhata");
  const [bills, setBills] = useState([]);
  const [sales, setSales] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [ledgerParties, setLedgerParties] = useState([]);
  const [selectedLedgerParty, setSelectedLedgerParty] = useState(null);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [totalReceivable, setTotalReceivable] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);
  const [ledgerPartyForm, setLedgerPartyForm] = useState({
    name: '',
    nameUrdu: '',
    type: 'customer',
    phone: '',
    address: '',
    openingBalance: '',
    balanceType: 'receivable'
  });
  const [ledgerTransactionForm, setLedgerTransactionForm] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    description: '',
    type: 'debit',
    amount: ''
  });

  // Ref for translation debounce timer
  const translationTimerRef = useRef(null);
  const [companyLanguage, setCompanyLanguage] = useState('ur');
  const [formData, setFormData] = useState({
    shopName: "",
    logo: "",
    address: "",
    contactNumber: "",
    contacts: [],
    billItemOptions: [],
    billItemFormMode: 'list',
    billFormStaticItemName: '',
    billFormStaticUnit: '',
    availableUnits: ['pali', 'paiti', 'units', 'bags', 'crates']
  });
  const [persons, setPersons] = useState([]);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [personForm, setPersonForm] = useState({
    name: "",
    contactNumber: ""
  });
  const [editingPerson, setEditingPerson] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [showEditBillModal, setShowEditBillModal] = useState(false);
  const [billFormData, setBillFormData] = useState({
    serialNumber: '',
    customerPartyId: '',
    customerName: '',
    customerNameUrdu: '',
    date: new Date().toLocaleDateString('en-CA'),
    receivedMessage: 'Vehicle Number',
    receivedMessageUrdu: 'گاڑی نمبر',
    goodsMessage: 'Your goods via consignment/bill of lading',
    goodsMessageUrdu: 'آپکا مال بذریعہ بلٹی',
    expenses: {},
    sales: {}
  });
  const [expenseFields, setExpenseFields] = useState([
    { fieldName: 'Vehicle', fieldNameUrdu: 'گاڑی', fieldType: 'number', isRequired: false, order: 0 },
    { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', isRequired: false, order: 2 },
    { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', isRequired: false, order: 3 },
    { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', isRequired: false, order: 4 },
    { fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ', fieldType: 'number', isRequired: false, order: 5 },
    { fieldName: 'Market Fee', fieldNameUrdu: 'مارکیٹ فیس', fieldType: 'number', isRequired: false, order: 6 }
  ]);
  const [salesFields, setSalesFields] = useState([
    { fieldName: 'Raw Sale', fieldNameUrdu: 'خام بِکری', fieldType: 'number', isRequired: true, order: 0 },
    { fieldName: 'Expenses', fieldNameUrdu: 'اخراجات', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Net Sale', fieldNameUrdu: 'پختہ بِکری', fieldType: 'number', isRequired: false, order: 2 }
  ]);

  const [editingLedgerTransactionId, setEditingLedgerTransactionId] = useState(null);
  const [customerSearchFilter, setCustomerSearchFilter] = useState('');
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [supplierSearchFilter, setSupplierSearchFilter] = useState('');
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingPartyId, setEditingPartyId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankBalances, setBankBalances] = useState({});
  const [chartData, setChartData] = useState([]);
  const [kpiData, setKpiData] = useState({
    totalBills: 0,
    totalSales: 0,
    totalRecoveries: 0,
    totalExpenses: 0,
    billsChange: 0,
    salesChange: 0,
    recoveriesChange: 0,
    expensesChange: 0
  });

  // Predefined options for Urdu goods message (مال کا پیغام) - unit types
  const goodsMessageUrduOptions = [
    'یونٹس',
    'بکس',
    'بوریاں'
  ];

  // Ledger helpers
  const fetchLedgerParties = async (type = '') => {
    try {
      setIsLoadingLedger(true);
      const token = localStorage.getItem('companyToken');
      const response = await api.get(`/ledger/parties${type ? `?type=${type}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const parties = response.data.parties || [];
      setLedgerParties(parties);

      // Calculate total receivable and payable
      // Receivable = positive balances (we are owed money)
      // Payable = negative balances (we owe money)
      let receivable = 0;
      let payable = 0;
      parties.forEach(party => {
        const balance = party.currentBalance || 0;
        if (balance > 0) {
          receivable += balance;
        } else if (balance < 0) {
          payable += Math.abs(balance);
        }
      });
      setTotalReceivable(receivable);
      setTotalPayable(payable);

      // Don't auto-select first party - let user click to select
      setSelectedLedgerParty(null);
      setLedgerTransactions([]);
    } catch (error) {
      console.error('Fetch ledger parties error:', error);
      toast.error(companyLanguage === 'ur' ? 'کھاتا پارٹیز نہیں مل سکیں' : 'Failed to load ledger parties');
    } finally {
      setIsLoadingLedger(false);
    }
  };

  const fetchLedgerTransactions = async (partyId) => {
    if (!partyId) return;
    try {
      setIsLoadingLedger(true);
      const token = localStorage.getItem('companyToken');
      const response = await api.get(`/ledger/parties/${partyId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLedgerParty(response.data.party);
      const transactions = response.data.transactions || [];
      console.log('Fetched ledger transactions:', transactions);
      console.log('Transactions with saleId:', transactions.filter(tx => tx.saleId));
      setLedgerTransactions(transactions);
    } catch (error) {
      console.error('Fetch ledger transactions error:', error);
      toast.error(companyLanguage === 'ur' ? 'ٹرانزیکشنز نہیں مل سکیں' : 'Failed to load transactions');
    } finally {
      setIsLoadingLedger(false);
    }
  };

  const handleEditLedgerTransaction = (tx) => {
    setEditingLedgerTransactionId(tx._id);
    setLedgerTransactionForm({
      date: tx.date ? new Date(tx.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: tx.description || '',
      type: tx.type || 'debit',
      amount: tx.amount != null ? String(tx.amount) : ''
    });
  };

  const handleDeleteLedgerTransaction = async (transactionId) => {
    if (!transactionId || !selectedLedgerParty) return;
    const confirmMsg =
      companyLanguage === 'ur'
        ? 'کیا آپ واقعی اس اندراج کو ختم کرنا چاہتے ہیں؟'
        : 'Are you sure you want to delete this entry?';
    if (!window.confirm(confirmMsg)) return;

    try {
      const token = getToken();
      await api.delete(`/ledger/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'اندراج ختم ہو گیا' : 'Entry deleted');
      // Refresh transactions
      await fetchLedgerTransactions(selectedLedgerParty._id);
    } catch (error) {
      console.error('Delete ledger transaction error:', error);
      toast.error(companyLanguage === 'ur' ? 'اندراج ختم نہیں ہو سکا' : 'Failed to delete entry');
    }
  };

  // Auto-transliterate English to Urdu script (Google Translate style)
  // Converts English names/words to Urdu script matching Google Translate's transliteration
  const transliterateToUrdu = (text) => {
    if (!text || text.trim() === '') return '';

    // Consonants mapping
    const consonants = {
      'b': 'ب', 'B': 'ب', 'c': 'س', 'C': 'س', 'd': 'د', 'D': 'د',
      'f': 'ف', 'F': 'ف', 'g': 'گ', 'G': 'گ', 'h': 'ہ', 'H': 'ہ',
      'j': 'ج', 'J': 'ج', 'k': 'ک', 'K': 'ک', 'l': 'ل', 'L': 'ل',
      'm': 'م', 'M': 'م', 'n': 'ن', 'N': 'ن', 'p': 'پ', 'P': 'پ',
      'q': 'ق', 'Q': 'ق', 'r': 'ر', 'R': 'ر', 's': 'س', 'S': 'س',
      't': 'ت', 'T': 'ت', 'v': 'و', 'V': 'و', 'w': 'و', 'W': 'و',
      'x': 'کس', 'X': 'کس', 'y': 'ی', 'Y': 'ی', 'z': 'ز', 'Z': 'ز'
    };

    // Special combinations
    const specialCombos = {
      'ch': 'چ', 'sh': 'ش', 'th': 'تھ', 'ph': 'پھ', 'gh': 'غ',
      'kh': 'خ', 'dh': 'دھ', 'bh': 'بھ', 'zh': 'ژ', 'rh': 'ڑھ',
      'ia': 'یا', 'ai': 'ائ', 'au': 'اؤ', 'ei': 'ئی', 'ou': 'ؤ',
      'aa': 'آ', 'ee': 'ی', 'ii': 'ی', 'oo': 'و', 'uu': 'یو'
    };

    let result = '';
    let i = 0;
    const lowerText = text.toLowerCase();
    const isVowel = (c) => ['a', 'e', 'i', 'o', 'u'].includes(c.toLowerCase());
    const isConsonant = (c) => consonants[c] !== undefined;

    while (i < text.length) {
      const char = text[i];
      const nextChar = i + 1 < text.length ? lowerText[i + 1] : '';
      const prevChar = i > 0 ? lowerText[i - 1] : '';
      const twoChars = i + 1 < text.length ? lowerText.substring(i, i + 2) : '';
      const threeChars = i + 2 < text.length ? lowerText.substring(i, i + 3) : '';

      // Check special combinations first
      if (twoChars && specialCombos[twoChars]) {
        result += specialCombos[twoChars];
        i += 2;
        continue;
      }

      // Handle "sa" at start -> "ص" (for "safdar" -> "صفدر")
      if ((char === 's' || char === 'S') && nextChar === 'a' && i === 0) {
        result += 'ص';
        i += 2; // Skip both 's' and 'a'
        continue;
      }

      // Handle "sa" in middle -> "ص" (skip the 'a')
      if ((char === 's' || char === 'S') && nextChar === 'a' && isConsonant(lowerText[i + 2] || '')) {
        result += 'ص';
        i += 2; // Skip both 's' and 'a'
        continue;
      }

      // Handle "za" -> "ض" (for "zia" -> "ضیا")
      if ((char === 'z' || char === 'Z') && nextChar === 'i') {
        result += 'ض';
        i++; // Skip 'z', keep 'i' for next iteration
        continue;
      }

      // Handle "ia" -> "یا"
      if ((char === 'i' || char === 'I') && nextChar === 'a') {
        result += 'یا';
        i += 2;
        continue;
      }

      // Handle vowels: only add if at start or after another vowel
      if (isVowel(char)) {
        if (i === 0) {
          // Vowel at start
          if (char === 'a' || char === 'A') result += 'ا';
          else if (char === 'e' || char === 'E') result += 'ی';
          else if (char === 'i' || char === 'I') result += 'ی';
          else if (char === 'o' || char === 'O') result += 'او';
          else if (char === 'u' || char === 'U') result += 'یو';
        } else if (isVowel(prevChar)) {
          // Vowel after vowel
          if (char === 'a' || char === 'A') result += 'ا';
          else if (char === 'e' || char === 'E') result += 'ی';
          else if (char === 'i' || char === 'I') result += 'ی';
          else if (char === 'o' || char === 'O') result += 'و';
          else if (char === 'u' || char === 'U') result += 'و';
        }
        // Skip 'a' after consonant (implicit vowel in Urdu)
        // Skip other vowels after consonant unless they're part of special combos
        i++;
        continue;
      }

      // Handle consonants
      if (isConsonant(char)) {
        result += consonants[char];
        i++;
        continue;
      }

      // Handle numbers
      const numbers = { '0': '۰', '1': '۱', '2': '۲', '3': '۳', '4': '۴', '5': '۵', '6': '۶', '7': '۷', '8': '۸', '9': '۹' };
      if (numbers[char]) {
        result += numbers[char];
        i++;
        continue;
      }

      // Keep other characters as is
      result += char;
      i++;
    }

    // Post-process fixes
    // Fix "صا" -> "ص" (remove extra 'ا' after 'ص')
    result = result.replace(/صا/g, 'ص');
    // Fix "صف" pattern (already handled, but ensure)
    result = result.replace(/سف/g, 'صف');
    // Fix "ضیا" pattern
    result = result.replace(/زیا/g, 'ضیا');
    result = result.replace(/زی/g, 'ضی');

    return result;
  };

  const handleLedgerPartyFormChange = (e) => {
    const { name, value } = e.target;

    setLedgerPartyForm((prev) => ({
      ...prev,
      [name]: value
    }));

    // Auto-transliteration removed - user will manually enter both English and Urdu names
  };

  const handleCreateLedgerParty = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      // Determine type based on active tab
      const partyType = (activeTab === 'supplierKhata' || activeLedgerTab === 'supplierKhata') ? 'supplier' : 'customer';

      // Safely parse opening balance (remove non-numeric characters, support decimals)
      const cleanedOpening = String(ledgerPartyForm.openingBalance || '')
        .replace(/[^0-9.-]/g, '');
      const openingValue = cleanedOpening ? parseFloat(cleanedOpening) : 0;

      const partyData = {
        ...ledgerPartyForm,
        type: partyType,
        openingBalance: isNaN(openingValue) ? 0 : openingValue
      };

      if (editingPartyId) {
        // Update existing party
        await api.put(
          `/ledger/parties/${editingPartyId}`,
          partyData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(companyLanguage === 'ur' ? 'پارٹی اپ ڈیٹ ہو گئی' : 'Party updated');
      } else {
        // Create new party
        await api.post(
          '/ledger/parties',
          partyData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(companyLanguage === 'ur' ? 'پارٹی بنا دی گئی' : 'Party created');
      }

      setLedgerPartyForm({
        name: '',
        nameUrdu: '',
        type: partyType,
        phone: '',
        address: '',
        openingBalance: '',
        balanceType: 'receivable'
      });
      setEditingPartyId(null);
      setShowCustomerForm(false);
      setShowSupplierForm(false);
      // Fetch all parties to ensure newly added parties appear
      fetchLedgerParties();
    } catch (error) {
      console.error('Create/Update ledger party error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی محفوظ نہیں ہو سکی' : 'Failed to save party');
    }
  };

  const handleEditLedgerParty = (party) => {
    setEditingPartyId(party._id);
    setLedgerPartyForm({
      name: party.name || '',
      nameUrdu: party.nameUrdu || '',
      type: party.type || 'customer',
      phone: party.phone || '',
      address: party.address || '',
      openingBalance: party.openingBalance || '',
      balanceType: party.balanceType || 'receivable'
    });
    if (party.type === 'customer') {
      setShowCustomerForm(true);
    } else {
      setShowSupplierForm(true);
    }
  };

  const handleDeleteLedgerParty = async (partyId) => {
    const confirmMsg = companyLanguage === 'ur'
      ? 'کیا آپ واقعی اس پارٹی کو حذف کرنا چاہتے ہیں؟'
      : 'Are you sure you want to delete this party?';

    if (!window.confirm(confirmMsg)) return;

    try {
      const token = getToken();
      await api.delete(`/ledger/parties/${partyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'پارٹی حذف ہو گئی' : 'Party deleted');
      // Fetch all parties to ensure list is up to date
      fetchLedgerParties();
      if (selectedLedgerParty && selectedLedgerParty._id === partyId) {
        setSelectedLedgerParty(null);
        setLedgerTransactions([]);
      }
    } catch (error) {
      console.error('Delete ledger party error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی حذف نہیں ہو سکی' : 'Failed to delete party');
    }
  };

  const handleLedgerTransactionFormChange = (e) => {
    const { name, value } = e.target;
    setLedgerTransactionForm((prev) => ({
      ...prev,
      [name]: name === 'amount' ? value.replace(/[^0-9.]/g, '') : value
    }));
  };

  const handleCreateLedgerTransaction = async (e) => {
    e.preventDefault();
    if (!selectedLedgerParty) {
      toast.error(companyLanguage === 'ur' ? 'پہلے پارٹی منتخب کریں' : 'Select a party first');
      return;
    }
    const amountNum = Number(ledgerTransactionForm.amount || 0);
    if (!amountNum) {
      toast.error(companyLanguage === 'ur' ? 'رقم درج کریں' : 'Enter amount');
      return;
    }
    try {
      const token = getToken();

      // If editing, update existing transaction
      if (editingLedgerTransactionId) {
        await api.put(
          `/ledger/transactions/${editingLedgerTransactionId}`,
          {
            date: ledgerTransactionForm.date,
            description: ledgerTransactionForm.description,
            type: ledgerTransactionForm.type,
            amount: amountNum
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(companyLanguage === 'ur' ? 'اندراج تبدیل ہو گیا' : 'Entry updated');
      } else {
        // Otherwise create new transaction
        await api.post(
          `/ledger/parties/${selectedLedgerParty._id}/transactions`,
          {
            date: ledgerTransactionForm.date,
            description: ledgerTransactionForm.description,
            type: ledgerTransactionForm.type,
            amount: amountNum
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(companyLanguage === 'ur' ? 'اندراج شامل ہو گیا' : 'Entry added');
      }

      setLedgerTransactionForm({
        date: new Date().toLocaleDateString('en-CA'),
        description: '',
        type: 'debit',
        amount: ''
      });
      setEditingLedgerTransactionId(null);
      fetchLedgerTransactions(selectedLedgerParty._id);
    } catch (error) {
      console.error('Create ledger transaction error:', error);
      toast.error(companyLanguage === 'ur' ? 'اندراج محفوظ نہیں ہو سکا' : 'Failed to save entry');
    }
  };


  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  // Get page permissions from company
  const getPagePermission = (pageKey) => {
    if (!company?.pagePermissions) return true; // Default to enabled if no permissions set
    // Convert Map to object if needed, or use directly if already object
    const permissions = company.pagePermissions instanceof Map
      ? Object.fromEntries(company.pagePermissions)
      : company.pagePermissions;
    return permissions[pageKey] !== false; // Default to true if not explicitly false
  };

  // Check if user is logged in
  useEffect(() => {
    const token = getToken();

    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.log("No token found, redirecting to login");
      }
      toast.error("Please login to access dashboard");
      navigate("/company/login", { replace: true });
      return;
    }

    // Always fetch profile first so dashboard type comes from server (avoids mix when admin changes dashboard)
    if (process.env.NODE_ENV === 'development') {
      console.log("Token found, fetching company profile...");
    }
    fetchCompanyProfile();
  }, [navigate]);

  // Handle tab changes from URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Load initial data
  const fetchCompanyProfile = async () => {
    try {
      // Phase 0: If we don't have cached data, show loader
      if (!company) setIsLoading(true);

      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      console.log("Fetching profile from /company/profile");
      const response = await api.get("/company/profile", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log("Profile fetched successfully:", response.data);

      let companyData = response.data.company;
      if (!companyData) {
        toast.error("Company data not found");
        return;
      }
      try {
        const rawLs = localStorage.getItem('companyData');
        if (rawLs) {
          const cached = JSON.parse(rawLs);
          if (cached && typeof cached === 'object') {
            if (companyData.billItemFormMode == null && cached.billItemFormMode != null) {
              companyData = { ...companyData, billItemFormMode: cached.billItemFormMode };
            }
            if (companyData.billFormStaticItemName === undefined && cached.billFormStaticItemName !== undefined) {
              companyData = { ...companyData, billFormStaticItemName: cached.billFormStaticItemName };
            }
            if (companyData.billFormStaticUnit === undefined && cached.billFormStaticUnit !== undefined) {
              companyData = { ...companyData, billFormStaticUnit: cached.billFormStaticUnit };
            }
            if ((!companyData.billItemOptions || companyData.billItemOptions.length === 0) && cached.billItemOptions?.length) {
              companyData = { ...companyData, billItemOptions: cached.billItemOptions };
            }
            if ((!companyData.itemNames || companyData.itemNames.length === 0) && cached.itemNames?.length) {
              companyData = { ...companyData, itemNames: cached.itemNames };
            }
            const cMode = String(cached.billItemFormMode || '').toLowerCase();
            const apiMode = String(companyData.billItemFormMode || '').toLowerCase();
            if (
              cMode === 'static' &&
              (cached.billFormStaticItemName || cached.billFormStaticUnit) &&
              (apiMode !== 'static' || (!companyData.billFormStaticItemName && !companyData.billFormStaticUnit))
            ) {
              companyData = {
                ...companyData,
                billItemFormMode: 'static',
                billFormStaticItemName: cached.billFormStaticItemName || companyData.billFormStaticItemName || '',
                billFormStaticUnit: cached.billFormStaticUnit || companyData.billFormStaticUnit || ''
              };
            }
          }
        }
      } catch (_) {
        /* ignore */
      }

      // Use only dashboardType from API (single source of truth)
      const raw = companyData?.dashboardType || 'main';
      const isMain = raw === 'main' || raw === 'english' || raw === 'en' || raw === 'dashboard-en';
      if (isMain) {
        navigate("/company/dashboard", { replace: true });
        return;
      }

      setCompany(companyData);
      setFormData({
        shopName: companyData.shopName || "",
        logo: companyData.logo || "",
        address: companyData.address || "",
        contactNumber: companyData.contactNumber || "",
        contacts: companyData.contacts && Array.isArray(companyData.contacts) && companyData.contacts.length > 0
          ? companyData.contacts
          : (companyData.contactNumber ? [{ name: '', number: companyData.contactNumber }] : []),
        billItemOptions: companyData.billItemOptions || [],
        billItemFormMode: String(companyData.billItemFormMode || 'list').toLowerCase() === 'static' ? 'static' : 'list',
        billFormStaticItemName: companyData.billFormStaticItemName || '',
        billFormStaticUnit: companyData.billFormStaticUnit || '',
        availableUnits: companyData.availableUnits && companyData.availableUnits.length > 0
          ? companyData.availableUnits
          : ['pali', 'paiti', 'units', 'bags', 'crates']
      });
      setPersons(companyData.persons || []);
      // FORCE URDU - Always use Urdu for this dashboard (DO NOT CHANGE)
      const lang = 'ur';
      setCompanyLanguage(lang);

      // Update localStorage with latest company data and language
      localStorage.setItem("companyData", JSON.stringify(companyData));
      localStorage.setItem("companyLanguage", lang);

      // Dispatch event to notify Header component
      window.dispatchEvent(new Event('companyDataUpdated'));

      // ── PHASE 1 DONE ──────────────────────────────────────────────
      // Show the dashboard immediately after profile is fetched.
      // The remaining data (bills, sales, recoveries, etc.) will load
      // in the background so the user sees the UI right away.
      setIsLoading(false);

      // ── PHASE 2: Background data loading ──────────────────────────
      // Fetch everything else without blocking the UI
      fetchBackgroundData();

    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error("Fetch profile error:", error);
        console.error("Error status:", error.response?.status);
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        if (process.env.NODE_ENV === 'development') {
          console.log("Unauthorized - clearing tokens and redirecting");
        }
        localStorage.removeItem("companyToken");
        localStorage.removeItem("companyData");
        toast.error("Session expired. Please login again.");
        navigate("/company/login", { replace: true });
      } else if (error.request) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Network error - backend not responding");
        }
        toast.error("Cannot connect to server. Please check if backend is running.");
      } else {
        toast.error("Failed to load profile. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBackgroundData = async () => {
    try {
      console.log("Starting Phase 2: Background data fetch (staggered)...");

      // Load most critical data first
      await fetchBills();

      // Small pause to let server breathe
      await new Promise(r => setTimeout(r, 100));
      await fetchSales();

      await new Promise(r => setTimeout(r, 100));
      // NOTE: fetchRecoveries + fetchPaymentMethods return data so we can compute bank balances
      const recoveriesData = await fetchRecoveries();

      await new Promise(r => setTimeout(r, 100));
      await fetchLedgerParties();

      await new Promise(r => setTimeout(r, 100));
      const paymentMethodsData = await fetchPaymentMethods();

      // Pre-compute bank balances once
      if ((paymentMethodsData || []).length > 0) {
        fetchAllBankBalances(paymentMethodsData, recoveriesData || []);
      }
      console.log("Phase 2 complete.");
    } catch (error) {
      console.error("Background data fetch error:", error);
    }
  };

  // Handle profile form change
  // Use company profile handlers hook - same structure, no changes
  const {
    handleChange,
    handleSaveProfile
  } = useCompanyProfile({
    formData,
    setFormData,
    setIsSaving,
    setCompany,
    persons,
    getToken
  });

  // Handle person form change
  const handlePersonChange = (e) => {
    setPersonForm({
      ...personForm,
      [e.target.name]: e.target.value
    });
  };

  // Add person
  const handleAddPerson = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();

      if (editingPerson) {
        // Update existing person
        const response = await api.put(
          `/company/persons/${editingPerson._id}`,
          personForm,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success("Person updated successfully!");
        fetchCompanyProfile();
      } else {
        // Add new person
        const response = await api.post(
          "/company/persons",
          personForm,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success("Person added successfully!");
        setPersons(response.data.company.persons);
      }

      setPersonForm({ name: "", contactNumber: "" });
      setEditingPerson(null);
      setShowAddPersonModal(false);
    } catch (error) {
      console.error("Add/Update person error:", error);
      toast.error(error.response?.data?.message || "Failed to save person");
    }
  };

  // Edit person
  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setPersonForm({
      name: person.name,
      contactNumber: person.contactNumber
    });
    setShowAddPersonModal(true);
  };

  // Delete person
  const handleDeletePerson = async (personId) => {
    if (!window.confirm("Are you sure you want to delete this person?")) {
      return;
    }

    try {
      const token = getToken();
      const response = await api.delete(`/company/persons/${personId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("Person deleted successfully!");
      setPersons(response.data.company.persons);
    } catch (error) {
      console.error("Delete person error:", error);
      toast.error(error.response?.data?.message || "Failed to delete person");
    }
  };

  // Fetch bills
  const fetchBills = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error("No token available for fetching bills");
        return [];
      }

      console.log("Fetching bills from /bill/company/my-bills?limit=2000");
      const response = await api.get("/bill/company/my-bills?limit=2000", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log("Bills response:", response.data);
      const billsData = response.data?.bills || response.data || [];
      console.log("Bills data received:", billsData);
      console.log("Bills count:", billsData.length);

      if (Array.isArray(billsData)) {
        setBills(billsData);
        console.log("Bills state updated, count:", billsData.length);
        return billsData;
      } else {
        console.error("Bills data is not an array:", billsData);
        setBills([]);
        return [];
      }
    } catch (error) {
      console.error("Fetch bills error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      toast.error(companyLanguage === 'ur' ? 'بلز لوڈ کرنے میں ناکام' : "Failed to load bills");
      setBills([]);
      return [];
    }
  };

  const fetchSales = async () => {
    try {
      const token = getToken();
      const response = await api.get("/sale/company/all?limit=2000", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const salesData = response.data.sales || [];
      setSales(salesData);
      return salesData;
    } catch (error) {
      console.error("Fetch sales error:", error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(companyLanguage === 'ur' ? 'سیلز لوڈ کرنے میں وقت لگ رہا ہے' : "Loading sales is taking longer than expected");
      } else {
        toast.error(companyLanguage === 'ur' ? 'سیلز لوڈ کرنے میں ناکام' : "Failed to load sales");
      }
      return [];
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return [];

      const response = await api.get("/payment-method/active");
      const methods = response.data.paymentMethods || [];
      setPaymentMethods(methods);
      return methods;
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      return [];
    }
  };

  // Fetch balances for all banks/payment methods.
  // IMPORTANT: recoveries are provided by the caller to avoid refetching them again (big performance win).
  const fetchAllBankBalances = async (methods = null, recoveriesData = []) => {
    try {
      const token = localStorage.getItem("companyToken");
      const banksToProcess = methods || paymentMethods;
      if (!token || banksToProcess.length === 0) return;

      // Fetch expenses + bank transactions in parallel
      const [allExpensesResponse, allBankTransactionsResponse] = await Promise.all([
        api.get(`/expense-ledger/company/all`, { headers: { Authorization: `Bearer ${token}` } }).catch((expenseError) => {
          console.error("Error fetching expenses:", expenseError);
          return { data: { expenses: [] } };
        }),
        api.get(`/bank/company/transactions/all`, { headers: { Authorization: `Bearer ${token}` } }).catch((txError) => {
          console.error("Error fetching bank transactions:", txError);
          return { data: { transactions: [] } };
        })
      ]);

      const recoveries = Array.isArray(recoveriesData) ? recoveriesData : [];
      const expenses = allExpensesResponse.data.expenses || [];
      const bankTransactions = allBankTransactionsResponse.data.transactions || [];

      // Pre-aggregate totals by payment method name to avoid O(banks * N) scans.
      const recoveryTotalsByMethod = new Map();
      for (const r of recoveries) {
        const key = (r.paymentMethod || '').trim();
        if (!key) continue;
        recoveryTotalsByMethod.set(key, (recoveryTotalsByMethod.get(key) || 0) + (r.recoveryAmount || 0));
      }

      const expenseTotalsByMethod = new Map();
      for (const e of expenses) {
        const key = (e.paymentMethod || '').trim();
        if (!key) continue;
        const expenseAmount =
          (e.travellingExpense || 0) +
          (e.refreshment || 0) +
          (e.cargo || 0) +
          (e.salaryExpense || 0);
        expenseTotalsByMethod.set(key, (expenseTotalsByMethod.get(key) || 0) + expenseAmount);
      }

      const bankTxNetByMethod = new Map();
      for (const tx of bankTransactions) {
        const key = (tx.paymentMethod || '').trim();
        if (!key) continue;
        const amt = tx.amount || 0;
        const net = tx.type === 'deposit' ? amt : -amt;
        bankTxNetByMethod.set(key, (bankTxNetByMethod.get(key) || 0) + net);
      }

      // Calculate balance for each bank using aggregated maps
      const balances = {};
      for (const bank of banksToProcess) {
        const bankName = (bank.name || '').trim();
        const bankNameUrdu = (bank.nameUrdu || bank.name || '').trim();

        const opening = bank.openingBalance || 0;
        const methodsToCheck = new Set([bankName, bankNameUrdu].filter(Boolean));

        let deposits = 0;
        let withdrawals = 0;
        let netTx = 0;
        for (const m of methodsToCheck) {
          deposits += recoveryTotalsByMethod.get(m) || 0;
          withdrawals += expenseTotalsByMethod.get(m) || 0;
          netTx += bankTxNetByMethod.get(m) || 0;
        }

        balances[bank._id] = opening + deposits - withdrawals + netTx;
      }

      setBankBalances(balances);
    } catch (error) {
      console.error("Fetch bank balances error:", error);
    }
  };

  const calculateChartData = useCallback(() => {
    try {
      // Get last 3 months data
      const months = [];
      const monthNames = companyLanguage === 'ur'
        ? ['اکتوبر', 'نومبر', 'دسمبر']
        : ['Oct', 'Nov', 'Dec'];

      for (let i = 2; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        // Filter bills for this month
        const monthBills = bills.filter(bill => {
          const billDate = new Date(bill.date);
          return billDate >= monthStart && billDate <= monthEnd;
        });

        // Filter sales for this month
        const monthSales = sales.filter(sale => {
          const saleDate = new Date(sale.date);
          return saleDate >= monthStart && saleDate <= monthEnd;
        });

        // Filter recoveries for this month
        const monthRecoveries = recoveries.filter(recovery => {
          const recoveryDate = new Date(recovery.date);
          return recoveryDate >= monthStart && recoveryDate <= monthEnd;
        });

        // Calculate income (sales + recoveries)
        const income = monthSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0) +
          monthRecoveries.reduce((sum, r) => sum + (r.recoveryAmount || 0), 0);

        // Calculate expenses (from bills)
        const expenses = monthBills.reduce((sum, b) => {
          const billExpenses = (b.expenses || {});
          return sum + (billExpenses.travellingExpense || 0) +
            (billExpenses.refreshment || 0) +
            (billExpenses.cargo || 0) +
            (billExpenses.salaryExpense || 0);
        }, 0);

        months.push({
          month: monthNames[2 - i],
          bills: monthBills.length,
          sales: monthSales.length,
          recoveries: monthRecoveries.length,
          income: Math.round(income),
          expenses: Math.round(expenses)
        });
      }

      setChartData(months);

      // Calculate KPI data
      const currentMonth = new Date();
      const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      const previousMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      const previousMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);

      const currentBills = bills.filter(b => {
        const d = new Date(b.date);
        return d >= currentMonthStart && d <= currentMonthEnd;
      }).length;

      const previousBills = bills.filter(b => {
        const d = new Date(b.date);
        return d >= previousMonthStart && d <= previousMonthEnd;
      }).length;

      const currentSales = sales.filter(s => {
        const d = new Date(s.date);
        return d >= currentMonthStart && d <= currentMonthEnd;
      }).length;

      const previousSales = sales.filter(s => {
        const d = new Date(s.date);
        return d >= previousMonthStart && d <= previousMonthEnd;
      }).length;

      const currentRecoveries = recoveries.filter(r => {
        const d = new Date(r.date);
        return d >= currentMonthStart && d <= currentMonthEnd;
      }).length;

      const previousRecoveries = recoveries.filter(r => {
        const d = new Date(r.date);
        return d >= previousMonthStart && d <= previousMonthEnd;
      }).length;

      const currentExpenses = bills.filter(b => {
        const d = new Date(b.date);
        return d >= currentMonthStart && d <= currentMonthEnd;
      }).reduce((sum, b) => {
        const e = b.expenses || {};
        return sum + (e.travellingExpense || 0) + (e.refreshment || 0) + (e.cargo || 0) + (e.salaryExpense || 0);
      }, 0);

      const previousExpenses = bills.filter(b => {
        const d = new Date(b.date);
        return d >= previousMonthStart && d <= previousMonthEnd;
      }).reduce((sum, b) => {
        const e = b.expenses || {};
        return sum + (e.travellingExpense || 0) + (e.refreshment || 0) + (e.cargo || 0) + (e.salaryExpense || 0);
      }, 0);

      setKpiData({
        totalBills: bills.length,
        totalSales: sales.length,
        totalRecoveries: recoveries.length,
        totalExpenses: Math.round(bills.reduce((sum, b) => {
          const e = b.expenses || {};
          return sum + (e.travellingExpense || 0) + (e.refreshment || 0) + (e.cargo || 0) + (e.salaryExpense || 0);
        }, 0)),
        billsChange: currentBills - previousBills,
        salesChange: currentSales - previousSales,
        recoveriesChange: currentRecoveries - previousRecoveries,
        expensesChange: Math.round(currentExpenses - previousExpenses)
      });

      console.log("Chart data calculated:", months);
      console.log("Chart data structure:", months.map(m => ({ month: m.month, income: m.income, expenses: m.expenses })));
      console.log("KPI data calculated:", {
        totalBills: bills.length,
        totalSales: sales.length,
        totalRecoveries: recoveries.length,
        billsChange: currentBills - previousBills,
        salesChange: currentSales - previousSales,
        recoveriesChange: currentRecoveries - previousRecoveries
      });
    } catch (error) {
      console.error("Calculate chart data error:", error);
    }
  }, [bills, sales, recoveries, companyLanguage]);

  useEffect(() => {
    if (bills.length > 0 || sales.length > 0 || recoveries.length > 0) {
      calculateChartData();
    }
  }, [bills, sales, recoveries, companyLanguage, calculateChartData]);

  // Check for recovery updates from sessionStorage on mount
  useEffect(() => {
    const checkRecoveryUpdate = async () => {
      const recoveryUpdateStr = sessionStorage.getItem('recoveryUpdated');
      if (recoveryUpdateStr) {
        try {
          const { recoveryId, customerPartyId, timestamp } = JSON.parse(recoveryUpdateStr);
          // Only process if update was recent (within last 5 seconds)
          if (timestamp && Date.now() - timestamp < 5000) {
            console.log('Recovery update detected from sessionStorage:', { recoveryId, customerPartyId });

            // Refresh recoveries
            await fetchRecoveries();

            // Refresh ledger parties to update balances
            await fetchLedgerParties();

            // Refresh transactions if party is selected
            if (selectedLedgerParty) {
              await fetchLedgerTransactions(selectedLedgerParty._id);
            } else if (customerPartyId) {
              await fetchLedgerTransactions(customerPartyId);
            }
          }
        } catch (error) {
          console.error('Error processing recovery update:', error);
        } finally {
          // Clear the sessionStorage
          sessionStorage.removeItem('recoveryUpdated');
        }
      }
    };

    checkRecoveryUpdate();
  }, []);

  // Listen for recovery update events to refresh data
  useEffect(() => {
    const handleRecoveryUpdated = async (event) => {
      const { recoveryId, customerPartyId } = event.detail || {};

      console.log('Recovery updated event received:', { recoveryId, customerPartyId, selectedLedgerParty: selectedLedgerParty?._id });

      // Refresh recoveries first
      await fetchRecoveries();

      // Refresh ledger parties to update balances
      await fetchLedgerParties();

      // Always refresh transactions if a party is selected
      if (selectedLedgerParty) {
        // Refresh transactions for currently selected party to get updated recovery data
        await fetchLedgerTransactions(selectedLedgerParty._id);
      } else if (customerPartyId) {
        // If no party selected but we know which party was affected, refresh that party's transactions
        await fetchLedgerTransactions(customerPartyId);
      }
    };

    window.addEventListener('recoveryUpdated', handleRecoveryUpdated);

    return () => {
      window.removeEventListener('recoveryUpdated', handleRecoveryUpdated);
    };
  }, [selectedLedgerParty]);

  const fetchRecoveries = async () => {
    try {
      const token = getToken();
      const response = await api.get("/recovery/company/all?limit=2000", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const recoveriesData = response.data.recoveries || [];
      setRecoveries(recoveriesData);
      return recoveriesData;
    } catch (error) {
      console.error("Fetch recoveries error:", error);

      // Check for MongoDB connection errors
      const errorMessage = error.response?.data?.message || error.message || '';
      const isConnectionError = errorMessage.includes('initial connection') ||
        errorMessage.includes('bufferCommands') ||
        errorMessage.includes('MongooseError') ||
        error.response?.status === 500;

      if (isConnectionError) {
        const lang = companyLanguage || localStorage.getItem('companyLanguage') || 'ur';
        toast.error(
          lang === 'ur'
            ? 'ڈیٹابیس کنکشن کی خرابی۔ براہ کرم تھوڑی دیر بعد دوبارہ کوشش کریں'
            : 'Database connection error. Please try again in a moment',
          { autoClose: 5000 }
        );
      } else {
        const lang = companyLanguage || localStorage.getItem('companyLanguage') || 'ur';
        const errorMsg = error.response?.status === 500
          ? (lang === 'ur' ? 'ریکوریز لوڈ کرنے میں خرابی' : 'Error loading recoveries')
          : (lang === 'ur' ? 'ریکوریز لوڈ نہیں ہو سکیں' : 'Failed to load recoveries');
        toast.error(errorMsg, { autoClose: 3000 });
      }

      setRecoveries([]); // Set empty array on error
      return [];
    }
  };

  // Use bill handlers hook - same structure, no changes
  const {
    handleBillFormChange,
    handleBillCustomerChange,
    handleExpenseChange,
    handleSalesChange,
    handleExpenseKeyDown,
    handleSalesKeyDown,
    handleCreateBill,
    handleEditBill: handleEditBillOriginal,
    handleUpdateBill,
    handleDeleteBill
  } = useBillHandlers({
    billFormData,
    setBillFormData,
    setShowBillModal,
    setShowEditBillModal,
    editingBill,
    setEditingBill,
    companyLanguage,
    setIsSaving,
    ledgerParties,
    fetchBills,
    getToken,
    setExpenseFields,
    defaultExpenseFields: [
      { fieldName: 'Vehicle', fieldNameUrdu: 'گاڑی', fieldType: 'number', isRequired: false, order: 0 },
      { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', isRequired: false, order: 1 },
      { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', isRequired: false, order: 2 },
      { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', isRequired: false, order: 3 },
      { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', isRequired: false, order: 4 },
      { fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ', fieldType: 'number', isRequired: false, order: 5 },
      { fieldName: 'Market Fee', fieldNameUrdu: 'مارکیٹ فیس', fieldType: 'number', isRequired: false, order: 6 }
    ]
  });

  // Override handleEditBill to navigate to CreateBillPart2 page instead of opening modal
  const handleEditBillWrapper = (bill) => {
    navigate(`/bill/create-part2?edit=${bill._id}`);
  };

  // View bill handler - original implementation
  const handleViewBill = (billId) => {
    window.open(`/bill/view/${billId}`, '_blank');
  };

  // Delete bill
  const handleEditRecovery = (recovery) => {
    navigate(`/recovery/create?edit=${recovery._id}`);
  };

  const handleDeleteRecovery = async (recoveryId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی یہ ریکوری حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this recovery?')) {
      return;
    }
    try {
      const token = getToken();
      await api.delete(`/recovery/company/${recoveryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'ریکوری حذف ہو گئی' : 'Recovery deleted');
      fetchRecoveries();
      // Refresh ledger parties to update balances in sidebar
      fetchLedgerParties();
      // Refresh ledger transactions if a customer is selected
      if (selectedLedgerParty) {
        fetchLedgerTransactions(selectedLedgerParty._id);
      }
    } catch (error) {
      console.error("Delete recovery error:", error);
      toast.error(companyLanguage === 'ur' ? 'ریکوری حذف کرنے میں ناکام' : "Failed to delete recovery");
    }
  };

  // Sale handlers
  const handleEditSale = async (sale) => {
    // If sale has a serialNumber, find the bill with that serialNumber and navigate to bill edit page
    if (sale.serialNumber) {
      const saleSerialNumber = String(sale.serialNumber).trim();

      // Find bill with matching serialNumber
      const matchingBill = bills.find(bill => {
        const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
        return billSerialNumber === saleSerialNumber;
      });

      if (matchingBill) {
        // Navigate to bill edit page
        navigate(`/bill/create-part2?edit=${matchingBill._id}`);
        return;
      }

      // If bill not found in current bills array, try fetching it
      try {
        const token = getToken();
        if (token) {
          const billsResponse = await api.get('/bill/company/my-bills', {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allBills = billsResponse.data?.bills || [];

          const foundBill = allBills.find(bill => {
            const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
            return billSerialNumber === saleSerialNumber;
          });

          if (foundBill) {
            navigate(`/bill/create-part2?edit=${foundBill._id}`);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching bills to find matching bill:', error);
      }

      // If bill not found, show error and fallback to sale edit
      toast.error(companyLanguage === 'ur'
        ? 'اس سیل کا بل نہیں ملا'
        : 'Bill not found for this sale');
    }

    // Fallback: navigate to sale edit page if no serialNumber or bill not found
    navigate(`/sale/create?edit=${sale._id}`);
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی یہ سیل حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this sale?')) {
      return;
    }
    try {
      const token = getToken();
      await api.delete(`/sale/company/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'سیل حذف ہو گئی' : 'Sale deleted');
      fetchSales();
      // Refresh ledger transactions if a customer is selected
      if (selectedLedgerParty) {
        fetchLedgerTransactions(selectedLedgerParty._id);
      }
    } catch (error) {
      console.error("Delete sale error:", error);
      toast.error(companyLanguage === 'ur' ? 'سیل حذف کرنے میں ناکام' : "Failed to delete sale");
    }
  };


  // Logout
  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    navigate("/company/login");
    toast.info("Logged out successfully");
  };

  if (isLoading) {
    return (
      <LoadingScreen
        companyLanguage={companyLanguage}
        message={companyLanguage === "ur" ? "ڈیش بورڈ لوڈ ہو رہا ہے..." : "Loading dashboard..."}
      />
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Company Dashboard - Bexon</title>
        <style>{`
          @keyframes blob {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scale-in {
            from { transform: scale(0.95); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }
          .animate-scale-in {
            animation: scale-in 0.3s ease-out;
          }
        `}</style>
      </Helmet>

      <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
        {/* Modern Animated Background Pattern */}
        <div className="fixed inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(34, 197, 94, 0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-green-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>

        {/* Header */}
        <div className="relative z-10">
          <DashboardHeader
            company={company}
            companyLanguage={companyLanguage}
            onLogout={handleLogout}
            bills={bills}
            sales={sales}
            recoveries={recoveries}
            persons={persons}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 pt-6 pb-8">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <OverviewTab
              paymentMethods={paymentMethods}
              bankBalances={bankBalances}
              setActiveTab={setActiveTab}
              setActiveLedgerTab={setActiveLedgerTab}
              companyLanguage={companyLanguage}
              kpiData={kpiData}
              chartData={chartData}
              totalReceivable={totalReceivable}
              totalPayable={totalPayable}
              company={company}
            />
          )}

          {/* Sales Tab */}
          {activeTab === "sales" && (
            <SalesTab
              sales={sales}
              companyLanguage={companyLanguage}
              handleEditSale={handleEditSale}
              handleDeleteSale={handleDeleteSale}
              bills={bills}
              company={company}
            />
          )}

          {activeTab === "recoveries" && (
            <RecoveriesTab
              recoveries={recoveries}
              ledgerParties={ledgerParties}
              companyLanguage={companyLanguage}
              handleEditRecovery={handleEditRecovery}
              handleDeleteRecovery={handleDeleteRecovery}
            />
          )}

          {/* Ledgers Tab */}
          {activeTab === "ledgers" && (
            <div className="space-y-6">
              {/* Ledger Sub-Tabs */}
              <div className="bg-white rounded-xl shadow-xl p-6 border border-green-100 print:hidden backdrop-blur-sm bg-opacity-95">
                <div className="flex flex-wrap gap-3 overflow-x-auto">
                  {getPagePermission('customerKhata') && (
                    <button
                      onClick={() => {
                        setActiveLedgerTab("customerKhata");
                        setSelectedLedgerParty(null);
                        setLedgerTransactions([]);
                        // Fetch all parties to ensure we get newly added customers
                        fetchLedgerParties();
                      }}
                      className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all transform hover:scale-105 ${activeLedgerTab === "customerKhata"
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-200"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'گاہک کھاتا' : 'Customer Ledger'}
                    </button>
                  )}
                  {getPagePermission('supplierKhata') && (
                    <button
                      onClick={() => {
                        setActiveLedgerTab("supplierKhata");
                        setSelectedLedgerParty(null);
                        setLedgerTransactions([]);
                        // Fetch all parties to ensure we get newly added suppliers
                        fetchLedgerParties();
                      }}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "supplierKhata"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'سپلائر کھاتا' : 'Supplier Ledger'}
                    </button>
                  )}
                  {getPagePermission('expenseLedger') && (
                    <button
                      onClick={() => setActiveLedgerTab("expenseLedger")}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "expenseLedger"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}
                    </button>
                  )}
                  {getPagePermission('bankKhata') && (
                    <button
                      onClick={() => setActiveLedgerTab("bankKhata")}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "bankKhata"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'بینک کھاتا' : 'Bank Ledger'}
                    </button>
                  )}
                  {getPagePermission('receivableKhata') && (
                    <button
                      onClick={() => {
                        setActiveLedgerTab("receivableKhata");
                      }}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "receivableKhata"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'قابل وصول کھاتہ' : 'Receivable Ledger'}
                    </button>
                  )}
                  {getPagePermission('payableKhata') && (
                    <button
                      onClick={() => {
                        setActiveLedgerTab("payableKhata");
                      }}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "payableKhata"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'قابل ادائیگی کھاتہ' : 'Payable Ledger'}
                    </button>
                  )}
                  {getPagePermission('profitLoss') && (
                    <button
                      onClick={() => setActiveLedgerTab("profitLoss")}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeLedgerTab === "profitLoss"
                        ? "bg-brand-header text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    >
                      {companyLanguage === 'ur' ? 'منافع و نقصان' : 'Profit & Loss'}
                    </button>
                  )}
                </div>
              </div>

              {/* Ledger Content */}
              {activeLedgerTab === "customerKhata" && getPagePermission('customerKhata') && (
                <CustomerKhata
                  company={company}
                  companyLanguage={companyLanguage}
                  ledgerParties={ledgerParties}
                  customerSearchFilter={customerSearchFilter}
                  setCustomerSearchFilter={setCustomerSearchFilter}
                  showCustomerForm={showCustomerForm}
                  setShowCustomerForm={setShowCustomerForm}
                  ledgerPartyForm={ledgerPartyForm}
                  handleLedgerPartyFormChange={handleLedgerPartyFormChange}
                  handleCreateLedgerParty={handleCreateLedgerParty}
                  editingPartyId={editingPartyId}
                  setEditingPartyId={setEditingPartyId}
                  isLoadingLedger={isLoadingLedger}
                  selectedLedgerParty={selectedLedgerParty}
                  fetchLedgerTransactions={fetchLedgerTransactions}
                  handleEditLedgerParty={handleEditLedgerParty}
                  handleDeleteLedgerParty={handleDeleteLedgerParty}
                  ledgerTransactions={ledgerTransactions}
                  ledgerTransactionForm={ledgerTransactionForm}
                  handleLedgerTransactionFormChange={handleLedgerTransactionFormChange}
                  handleCreateLedgerTransaction={handleCreateLedgerTransaction}
                  activeTab={activeTab}
                  activeLedgerTab={activeLedgerTab}
                  setLedgerPartyForm={setLedgerPartyForm}
                  sales={sales}
                />
              )}
              {activeLedgerTab === "supplierKhata" && getPagePermission('supplierKhata') && (
                <SupplierKhata
                  company={company}
                  companyLanguage={companyLanguage}
                  ledgerParties={ledgerParties}
                  supplierSearchFilter={supplierSearchFilter}
                  setSupplierSearchFilter={setSupplierSearchFilter}
                  showSupplierForm={showSupplierForm}
                  setShowSupplierForm={setShowSupplierForm}
                  ledgerPartyForm={ledgerPartyForm}
                  handleLedgerPartyFormChange={handleLedgerPartyFormChange}
                  handleCreateLedgerParty={handleCreateLedgerParty}
                  editingPartyId={editingPartyId}
                  setEditingPartyId={setEditingPartyId}
                  isLoadingLedger={isLoadingLedger}
                  selectedLedgerParty={selectedLedgerParty}
                  fetchLedgerTransactions={fetchLedgerTransactions}
                  handleEditLedgerParty={handleEditLedgerParty}
                  handleDeleteLedgerParty={handleDeleteLedgerParty}
                  ledgerTransactions={ledgerTransactions}
                  ledgerTransactionForm={ledgerTransactionForm}
                  handleLedgerTransactionFormChange={handleLedgerTransactionFormChange}
                  handleCreateLedgerTransaction={handleCreateLedgerTransaction}
                  activeTab={activeTab}
                  activeLedgerTab={activeLedgerTab}
                  setLedgerPartyForm={setLedgerPartyForm}
                />
              )}
              {activeLedgerTab === "expenseLedger" && getPagePermission('expenseLedger') && (
                <ExpenseLedger companyLanguage={companyLanguage} />
              )}
              {activeLedgerTab === "bankKhata" && getPagePermission('bankKhata') && (
                <BankKhata companyLanguage={companyLanguage} />
              )}
              {activeLedgerTab === "receivableKhata" && getPagePermission('receivableKhata') && (
                <ReceivableKhata companyLanguage={companyLanguage} />
              )}
              {activeLedgerTab === "payableKhata" && getPagePermission('payableKhata') && (
                <PayableKhata companyLanguage={companyLanguage} />
              )}
              {activeLedgerTab === "profitLoss" && getPagePermission('profitLoss') && (
                <ProfitLossAccount companyLanguage={companyLanguage} />
              )}
            </div>
          )}

          {/* Profile Settings Tab */}
          {activeTab === "profile" && (
            <ProfileTab
              companyLanguage={companyLanguage}
              formData={formData}
              handleChange={handleChange}
              handleSaveProfile={handleSaveProfile}
              isSaving={isSaving}
              setActiveTab={setActiveTab}
              company={company}
            />
          )}

          {/* Bills Tab */}
          {activeTab === "bills" && (
            <BillsTab
              bills={bills}
              companyLanguage={companyLanguage}
              navigate={navigate}
              handleEditBill={handleEditBillWrapper}
              handleDeleteBill={handleDeleteBill}
              handleViewBill={handleViewBill}
            />
          )}

          {/* Contact Persons Tab */}
          {activeTab === "persons" && (
            <PersonsTab
              persons={persons}
              companyLanguage={companyLanguage}
              setEditingPerson={setEditingPerson}
              setPersonForm={setPersonForm}
              setShowAddPersonModal={setShowAddPersonModal}
              handleEditPerson={handleEditPerson}
              handleDeletePerson={handleDeletePerson}
            />
          )}

          {/* Receipt Management Tab */}
          {activeTab === "receipts" && (
            <ReceiptManagement />
          )}
        </div>
      </div>

      {/* Create/Edit Bill Modal */}
      <CreateBillModal
        showBillModal={showBillModal || showEditBillModal}
        setShowBillModal={(val) => {
          if (!val) {
            setShowBillModal(false);
            setShowEditBillModal(false);
            setEditingBill(null);
          } else {
            setShowBillModal(val);
          }
        }}
        billFormData={billFormData}
        expenseFields={expenseFields}
        salesFields={salesFields}
        ledgerParties={ledgerParties}
        companyLanguage={companyLanguage}
        isSaving={isSaving}
        goodsMessageUrduOptions={goodsMessageUrduOptions}
        handleBillFormChange={handleBillFormChange}
        handleBillCustomerChange={handleBillCustomerChange}
        handleExpenseChange={handleExpenseChange}
        handleSalesChange={handleSalesChange}
        handleExpenseKeyDown={handleExpenseKeyDown}
        handleSalesKeyDown={handleSalesKeyDown}
        handleCreateBill={handleCreateBill}
        handleUpdateBill={handleUpdateBill}
        isEdit={showEditBillModal}
        editingBill={editingBill}
      />

      {/* Add/Edit Person Modal */}
      {showAddPersonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-scale-in">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <h2 className={`text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {editingPerson ? (companyLanguage === 'ur' ? 'تبدیل کریں' : "Edit Contact Person") : (companyLanguage === 'ur' ? 'شامل کریں' : "Add Contact Person")}
              </h2>
              <button
                onClick={() => {
                  setShowAddPersonModal(false);
                  setPersonForm({ name: "", contactNumber: "" });
                  setEditingPerson(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddPerson} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {companyLanguage === 'ur' ? 'نام *' : 'Name *'}
                </label>
                <input
                  type="text"
                  name="name"
                  value={personForm.name}
                  onChange={handlePersonChange}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder={companyLanguage === 'ur' ? 'نام لکھیں' : "Enter person name"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {companyLanguage === 'ur' ? 'رابطہ نمبر *' : 'Contact Number *'}
                </label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={personForm.contactNumber}
                  onChange={handlePersonChange}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="+1 (234) 567-8900"
                />
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPersonModal(false);
                    setPersonForm({ name: "", contactNumber: "" });
                    setEditingPerson(null);
                  }}
                  className={`flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                >
                  {companyLanguage === 'ur' ? 'منسوخ' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className={`flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl transition-all transform hover:scale-105 hover:shadow-lg font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                >
                  {editingPerson ? (companyLanguage === 'ur' ? 'تبدیل کریں' : "Update Person") : (companyLanguage === 'ur' ? 'شامل کریں' : "Add Person")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default CompanyDashboardPart2;

